import React, { useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import 'react-csv-importer/dist/index.css';

import {
    Box,
    Button,
    CircularProgress,
    Grid,
    LinearProgress,
    Modal,
    Stack,
    Typography,
    Alert,
    IconButton,
} from '@mui/material';
import { useParams } from 'react-router-dom';

import { Importer, ImporterField } from 'react-csv-importer';
import ExcelJS from 'exceljs';
import csvDemo from '../../../assets/videos/csvdemo.mp4';
import MainCard from 'ui-component/cards/MainCard';
import { GridCloseIcon } from '@mui/x-data-grid';

const GET_INVITATION_FORM = gql`
    query GetInvitationForm($eventId: String!, $role: ROLE!) {
        form(eventId: $eventId, role: $role) {
            fields {
                name
                type
                label
                required
            }
        }
    }
`;

// System columns that should be excluded from mapping
const SYSTEM_COLUMNS = [
    'status',
    'paidSeats',
    'freeSeats',
    'discount',
    'no_of_participant',
    'serial',
    'id',
    'created',
    'regLink',
    'regDevice',
    'isAccepted',
    'isInvitationSend',
    'uploadedBy',
    'uploadedByEmail',
    'payment_status',
    'amount',
    'product_name',
    'promoCode',
    'payment',
    'invitation_mode',
    'uid',
    'invitation_id',
    'event_id',
    'user_role',
    'payment_option',
    'masterclass_sessions',
    'invitationStatus',
    'responseTimestamp',
];

const UPLOAD_INVITATION_DATA = gql`
    mutation UploadInvitationData($eventId: String!, $role: ROLE!, $data: [String!]!) {
        uploadInvitationData(eventId: $eventId, role: $role, data: $data) {
            success
            message
            totalRows
            successfulRows
            failedRows
            failedData {
                rowNumber
                originalData
                errorReason
                fieldErrors {
                    field
                    value
                    error
                }
            }
            uploadHistoryId
        }
    }
`;

const GET_UPLOAD_HISTORY = gql`
    query GetUploadHistory($eventId: String!, $role: ROLE!) {
        uploadHistory(eventId: $eventId, role: $role) {
            totalUploads
            successfulUploads
            failedUploads
            lastUploadDate
            failedData {
                id
                uniqueId
                eventId
                role
                uploaderBy
                uploaderEmail
                originalData
                errorReason
                failedAt
                retryCount
                status
            }
        }
    }
`;

export default function UploadInvitationLink({ eventId, role, onUploadSuccess }) {
    // State for upload history
    const [showHistory, setShowHistory] = useState(false);
    const [selectedHistoryItem, setSelectedHistoryItem] = useState(null);
    const [currentUploadResult, setCurrentUploadResult] = useState(null);

    // Query for upload history
    const {
        data: historyData,
        loading: historyLoading,
        refetch: refetchHistory,
    } = useQuery(GET_UPLOAD_HISTORY, {
        variables: { eventId, role },
        skip: !showHistory,
    });

    const [uploadData, { loading: uploading }] = useMutation(UPLOAD_INVITATION_DATA, {
        onCompleted: (data) => {
            const result = data.uploadInvitationData;

            // Store the upload result for display
            setCurrentUploadResult(result);

            // Check if upload was successful (even with some failed rows)
            const isSuccessful = result.successfulRows > 0;

            if (isSuccessful) {
                setMessage({
                    type: 'success',
                    text: `✅ Upload successful! ${
                        result.successfulRows
                    } rows uploaded successfully${
                        result.failedRows > 0 ? `, ${result.failedRows} rows failed` : ''
                    }`,
                });
                setUploadedCount(result.successfulRows);
                setUploadSuccess(true);

                // Notify parent component to refetch invitation data
                if (onUploadSuccess) {
                    onUploadSuccess();
                }

                // Show failed rows if any
                if (result.failedRows > 0) {
                    return;
                }
            } else {
                setMessage({
                    type: 'error',
                    text: `❌ Upload failed! ${result.message}`,
                });
                setUploadSuccess(false);
            }

            // Refresh upload history
            if (showHistory) {
                refetchHistory();
            }

            setTimeout(() => setMessage({ type: '', text: '' }), 15000);
        },
        onError: (error) => {
            setMessage({
                type: 'error',
                text: error.message || 'Failed to upload data',
            });
            setUploadError(error.message || 'Failed to upload data');
            setUploadSuccess(false);
            setTimeout(() => setMessage({ type: '', text: '' }), 10000);
        },
    });
    const [showTutorial, setShowTutorial] = useState(false);
    const [uploadedCount, setUploadedCount] = useState(0);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [uploadError, setUploadError] = useState(null);
    const [uploadSuccess, setUploadSuccess] = useState(false);

    const { data: formData, loading: formLoading } = useQuery(GET_INVITATION_FORM, {
        variables: { eventId, role },
    });

    const handleUpload = async (rows) => {
        // Validate rows data
        if (!rows || !Array.isArray(rows)) {
            setMessage({ type: 'error', text: 'Invalid data format received' });
            setTimeout(() => setMessage({ type: '', text: '' }), 5000);
            return;
        }

        if (rows.length === 0) {
            setMessage({ type: 'error', text: 'No data to upload' });
            setTimeout(() => setMessage({ type: '', text: '' }), 5000);
            return;
        }

        // Let Apollo Client handle the mutation with onCompleted/onError callbacks
        await uploadData({
            variables: {
                eventId,
                role,
                data: rows.map((row) => JSON.stringify(row)),
            },
        });
    };

    const downloadSampleData = () => {
        if (!formData?.form?.fields || !Array.isArray(formData.form.fields)) {
            return;
        }

        // Filter out system columns, only show data columns
        const fields = formData.form.fields.filter((field) => !SYSTEM_COLUMNS.includes(field.name));

        if (fields.length === 0) {
            return;
        }

        const sampleData = [
            fields.reduce((acc, field) => {
                acc[field.name] = `Sample ${field.label || field.name}`;
                return acc;
            }, {}),
        ];

        const csvContent = [
            fields.map((field) => field.name).join(','),
            ...sampleData.map((row) =>
                fields.map((field) => `"${row[field.name] || ''}"`).join(',')
            ),
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${role}_invitation_sample.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
    };

    // Function to download failed data as CSV
    const downloadFailedData = (failedRows) => {
        if (!failedRows || failedRows.length === 0) return;

        try {
            // Parse the original data and create CSV
            const csvData = failedRows.map((row) => {
                const originalData = JSON.parse(row.originalData);
                return {
                    'Row Number': row.rowNumber,
                    'Error Reason': row.errorReason,
                    ...originalData,
                };
            });

            // Convert to CSV
            const headers = Object.keys(csvData[0]);
            const csvContent =
                'data:text/csv;charset=utf-8,' +
                headers.join(',') +
                '\n' +
                csvData
                    .map((row) =>
                        headers.map((header) => JSON.stringify(row[header] || '')).join(',')
                    )
                    .join('\n');

            const encodedUri = encodeURI(csvContent);
            const link = document.createElement('a');
            link.setAttribute('href', encodedUri);
            link.setAttribute(
                'download',
                `${role}_failed_data_${new Date().toISOString().split('T')[0]}.csv`
            );
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error('Error downloading failed data:', error);
        }
    };

    // Function to download successful data as CSV
    const downloadSuccessfulData = (successfulRows) => {
        if (!successfulRows || successfulRows.length === 0) return;

        try {
            const headers = Object.keys(successfulRows[0]);
            const csvContent =
                'data:text/csv;charset=utf-8,' +
                headers.join(',') +
                '\n' +
                successfulRows
                    .map((row) =>
                        headers.map((header) => JSON.stringify(row[header] || '')).join(',')
                    )
                    .join('\n');

            const encodedUri = encodeURI(csvContent);
            const link = document.createElement('a');
            link.setAttribute('href', encodedUri);
            link.setAttribute(
                'download',
                `${role}_successful_data_${new Date().toISOString().split('T')[0]}.csv`
            );
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error('Error downloading successful data:', error);
        }
    };

    if (formLoading) return <LinearProgress />;

    const fields = formData?.form?.fields || [];
    console.log('Frontend fields:', fields);
    console.log('Fields length:', fields.length);

    return (
        <Box>
            <MainCard
                title={`Upload ${role.charAt(0).toUpperCase() + role.slice(1)} Invitation Data`}
            >
                {/* Tutorial Section */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        How to Upload Invitation Data
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <Box sx={{ p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
                                <Typography variant="subtitle1" gutterBottom>
                                    📋 Step 1: Prepare Your Data
                                </Typography>
                                <Typography variant="body2" paragraph>
                                    Ensure your CSV file has the correct column headers matching the
                                    form fields below.
                                </Typography>
                                <Typography variant="subtitle1" gutterBottom>
                                    📤 Step 2: Upload File
                                </Typography>
                                <Typography variant="body2" paragraph>
                                    Use the upload area below to import your CSV or Excel file.
                                </Typography>
                                <Typography variant="subtitle1" gutterBottom>
                                    ✉️ Step 3: Send Invitations
                                </Typography>
                                <Typography variant="body2">
                                    After upload, go to &quot;My Invitations&quot; tab to send
                                    invitation emails.
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Box sx={{ p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
                                <Typography variant="subtitle1" gutterBottom>
                                    🎥 Tutorial Video
                                </Typography>
                                <video
                                    controls
                                    width="100%"
                                    style={{ borderRadius: '4px' }}
                                    src={csvDemo}
                                >
                                    Your browser does not support the video tag.
                                </video>
                                <Button
                                    variant="outlined"
                                    onClick={() => setShowTutorial(true)}
                                    sx={{ mt: 1 }}
                                    fullWidth
                                >
                                    Watch Full Tutorial
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>

                {/* Required Fields */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        Required Fields for {role.charAt(0).toUpperCase() + role.slice(1)}
                    </Typography>
                    <Grid container spacing={1}>
                        {fields
                            .filter((field) => !SYSTEM_COLUMNS.includes(field.name))
                            .map((field, index) => (
                                <Grid item xs={12} sm={6} md={4} key={index}>
                                    <Box
                                        sx={{
                                            p: 1,
                                            border: '1px solid #e0e0e0',
                                            borderRadius: 1,
                                            bgcolor: field.required ? '#fff3e0' : '#f5f5f5',
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            fontWeight={field.required ? 'bold' : 'normal'}
                                        >
                                            {field.name}
                                            {field.required && (
                                                <span style={{ color: 'red' }}> *</span>
                                            )}
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary">
                                            {field.type} - {field.label}
                                        </Typography>
                                    </Box>
                                </Grid>
                            ))}
                    </Grid>
                </Box>

                {/* Sample Data Download */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        Download Sample Data
                    </Typography>
                    <Button
                        variant="contained"
                        onClick={downloadSampleData}
                        disabled={!fields || fields.length === 0}
                    >
                        Download {role.charAt(0).toUpperCase() + role.slice(1)} Sample CSV
                    </Button>
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                        Use this sample file as a template for your data upload.
                    </Typography>
                </Box>

                {/* Upload Area */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        Upload Invitation Data
                    </Typography>
                    <Box
                        sx={{
                            border: '2px dashed #ccc',
                            borderRadius: 2,
                            p: 3,
                            textAlign: 'center',
                            bgcolor: '#fafafa',
                        }}
                    >
                        <Importer
                            dataHandler={handleUpload}
                            defaultNoHeader={false}
                            restartable={true}
                            onComplete={({ rows, startIndex }) => {
                                if (rows && Array.isArray(rows)) {
                                    console.log(
                                        `Started at ${startIndex}, imported ${rows.length} rows`
                                    );
                                } else {
                                    console.log(`Started at ${startIndex}, rows data:`, rows);
                                }
                            }}
                            onError={(err, { startIndex }) => {
                                console.error(`Error starting at ${startIndex}:`, err);
                            }}
                        >
                            {/* Use only dynamic fields from backend, no hardcoded fields */}
                            {fields && Array.isArray(fields) && fields.length > 0 ? (
                                fields.map((field, index) => (
                                    <ImporterField
                                        key={index}
                                        name={field.name}
                                        label={field.label || field.name}
                                        optional={!field.required}
                                    />
                                ))
                            ) : (
                                <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                                    <Typography variant="body2">
                                        No form fields available. Please check your connection.
                                    </Typography>
                                </Box>
                            )}
                        </Importer>
                    </Box>
                </Box>

                {/* Success/Error Messages */}
                {message.type && (
                    <Alert severity={message.type} sx={{ mb: 2 }}>
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                            {message.text}
                        </Typography>
                        {currentUploadResult && currentUploadResult.successfulRows > 0 && (
                            <Typography variant="body2" sx={{ mt: 1 }}>
                                📊 <strong>Summary:</strong> {currentUploadResult.totalRows} total
                                rows processed,
                                {currentUploadResult.successfulRows} successful,
                                {currentUploadResult.failedRows} failed
                            </Typography>
                        )}
                    </Alert>
                )}

                {/* Upload Status */}
                {uploading && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <CircularProgress size={20} />
                        <Typography>Uploading invitation data...</Typography>
                    </Box>
                )}

                {/* Current Upload Result */}
                {currentUploadResult && (
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Last Upload Result
                        </Typography>

                        {/* Summary Cards */}
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                            <Grid item xs={12} sm={4}>
                                <Box
                                    sx={{
                                        p: 2,
                                        bgcolor: 'info.light',
                                        borderRadius: 1,
                                        textAlign: 'center',
                                    }}
                                >
                                    <Typography variant="h4" color="white">
                                        {currentUploadResult.totalRows}
                                    </Typography>
                                    <Typography variant="body2" color="white">
                                        Total Rows
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <Box
                                    sx={{
                                        p: 2,
                                        bgcolor: 'success.light',
                                        borderRadius: 1,
                                        textAlign: 'center',
                                    }}
                                >
                                    <Typography variant="h4" color="white">
                                        {currentUploadResult.successfulRows}
                                    </Typography>
                                    <Typography variant="body2" color="white">
                                        Successful
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <Box
                                    sx={{
                                        p: 2,
                                        bgcolor: 'error.light',
                                        borderRadius: 1,
                                        textAlign: 'center',
                                    }}
                                >
                                    <Typography variant="h4" color="white">
                                        {currentUploadResult.failedRows}
                                    </Typography>
                                    <Typography variant="body2" color="white">
                                        Failed
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>

                        {/* Download Buttons for Current Upload */}
                        {currentUploadResult.failedRows > 0 && (
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="subtitle1" gutterBottom>
                                    Download Current Upload Data
                                </Typography>
                                <Stack direction="row" spacing={2}>
                                    {currentUploadResult.successfulRows > 0 && (
                                        <Button
                                            variant="outlined"
                                            color="success"
                                            onClick={() =>
                                                downloadSuccessfulData(
                                                    currentUploadResult.successfulData || []
                                                )
                                            }
                                        >
                                            Download Successful Rows
                                        </Button>
                                    )}
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        onClick={() =>
                                            downloadFailedData(currentUploadResult.failedData || [])
                                        }
                                    >
                                        Download Failed Rows
                                    </Button>
                                </Stack>
                            </Box>
                        )}

                        {/* Failed Data Details for Current Upload */}
                        {currentUploadResult.failedRows > 0 && currentUploadResult.failedData && (
                            <Box>
                                <Typography variant="subtitle1" gutterBottom>
                                    Failed Rows Details
                                </Typography>
                                <Box
                                    sx={{
                                        maxHeight: 300,
                                        overflow: 'auto',
                                        border: '1px solid #ddd',
                                        borderRadius: 1,
                                    }}
                                >
                                    {currentUploadResult.failedData.map((row, index) => (
                                        <Box
                                            key={index}
                                            sx={{
                                                p: 2,
                                                borderBottom: '1px solid #eee',
                                                '&:last-child': { borderBottom: 'none' },
                                            }}
                                        >
                                            <Typography variant="body2" color="error" gutterBottom>
                                                <strong>Row {row.rowNumber}:</strong>{' '}
                                                {row.errorReason}
                                            </Typography>
                                            {row.fieldErrors && row.fieldErrors.length > 0 && (
                                                <Box sx={{ ml: 2, mt: 1 }}>
                                                    {row.fieldErrors.map(
                                                        (fieldError, fieldIndex) => (
                                                            <Typography
                                                                key={fieldIndex}
                                                                variant="body2"
                                                                color="textSecondary"
                                                                sx={{ fontSize: '0.875rem' }}
                                                            >
                                                                <strong>{fieldError.field}:</strong>{' '}
                                                                {fieldError.value} -{' '}
                                                                {fieldError.error}
                                                            </Typography>
                                                        )
                                                    )}
                                                </Box>
                                            )}
                                            <Typography
                                                variant="body2"
                                                color="textSecondary"
                                                sx={{ fontSize: '0.875rem', mt: 1 }}
                                            >
                                                <strong>Original Data:</strong> {row.originalData}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        )}
                    </Box>
                )}

                {/* Upload History Section */}
                <Box sx={{ mt: 4 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 2,
                        }}
                    >
                        <Typography
                            variant="h6"
                            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                        >
                            📊 Upload History
                        </Typography>
                        <Button
                            variant="outlined"
                            onClick={() => {
                                setShowHistory(!showHistory);
                                if (!showHistory) {
                                    refetchHistory();
                                }
                            }}
                        >
                            {showHistory ? 'Hide History' : 'Show History'}
                        </Button>
                    </Box>

                    {showHistory && (
                        <Box>
                            {historyLoading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                                    <CircularProgress />
                                </Box>
                            ) : historyData?.uploadHistory ? (
                                <Box>
                                    {/* Summary Cards */}
                                    <Grid container spacing={2} sx={{ mb: 3 }}>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <Box
                                                sx={{
                                                    p: 3,
                                                    bgcolor: 'primary.light',
                                                    borderRadius: 2,
                                                    textAlign: 'center',
                                                    boxShadow: 2,
                                                }}
                                            >
                                                <Typography
                                                    variant="h3"
                                                    color="white"
                                                    sx={{ fontWeight: 'bold' }}
                                                >
                                                    {historyData.uploadHistory.totalUploads || 0}
                                                </Typography>
                                                <Typography
                                                    variant="body1"
                                                    color="white"
                                                    sx={{ fontWeight: 500 }}
                                                >
                                                    Total Uploads
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <Box
                                                sx={{
                                                    p: 3,
                                                    bgcolor: 'success.light',
                                                    borderRadius: 2,
                                                    textAlign: 'center',
                                                    boxShadow: 2,
                                                }}
                                            >
                                                <Typography
                                                    variant="h3"
                                                    color="white"
                                                    sx={{ fontWeight: 'bold' }}
                                                >
                                                    {historyData.uploadHistory.successfulUploads ||
                                                        0}
                                                </Typography>
                                                <Typography
                                                    variant="body1"
                                                    color="white"
                                                    sx={{ fontWeight: 500 }}
                                                >
                                                    Successful Rows
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <Box
                                                sx={{
                                                    p: 3,
                                                    bgcolor: 'error.light',
                                                    borderRadius: 2,
                                                    textAlign: 'center',
                                                    boxShadow: 2,
                                                }}
                                            >
                                                <Typography
                                                    variant="h3"
                                                    color="white"
                                                    sx={{ fontWeight: 'bold' }}
                                                >
                                                    {historyData.uploadHistory.failedUploads || 0}
                                                </Typography>
                                                <Typography
                                                    variant="body1"
                                                    color="white"
                                                    sx={{ fontWeight: 500 }}
                                                >
                                                    Failed Rows
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <Box
                                                sx={{
                                                    p: 3,
                                                    bgcolor: 'info.light',
                                                    borderRadius: 2,
                                                    textAlign: 'center',
                                                    boxShadow: 2,
                                                }}
                                            >
                                                <Typography
                                                    variant="h3"
                                                    color="white"
                                                    sx={{ fontWeight: 'bold' }}
                                                >
                                                    {(() => {
                                                        try {
                                                            if (
                                                                !historyData.uploadHistory
                                                                    .lastUploadDate
                                                            )
                                                                return 'N/A';

                                                            // Handle different date formats
                                                            let date;

                                                            // Check if it's a Unix timestamp (number)
                                                            if (
                                                                typeof historyData.uploadHistory
                                                                    .lastUploadDate === 'number' ||
                                                                /^\d+$/.test(
                                                                    historyData.uploadHistory
                                                                        .lastUploadDate
                                                                )
                                                            ) {
                                                                const timestamp = parseInt(
                                                                    historyData.uploadHistory
                                                                        .lastUploadDate
                                                                );
                                                                if (isNaN(timestamp))
                                                                    return 'Invalid Date';
                                                                date = new Date(timestamp);
                                                            }
                                                            // Check if it's a MySQL timestamp string
                                                            else if (
                                                                typeof historyData.uploadHistory
                                                                    .lastUploadDate === 'string'
                                                            ) {
                                                                // Handle MySQL timestamp format: "2024-01-15 10:30:00"
                                                                if (
                                                                    /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/.test(
                                                                        historyData.uploadHistory
                                                                            .lastUploadDate
                                                                    )
                                                                ) {
                                                                    date = new Date(
                                                                        historyData.uploadHistory.lastUploadDate
                                                                    );
                                                                }
                                                                // Handle ISO string format
                                                                else if (
                                                                    historyData.uploadHistory.lastUploadDate.includes(
                                                                        'T'
                                                                    ) ||
                                                                    historyData.uploadHistory.lastUploadDate.includes(
                                                                        'Z'
                                                                    )
                                                                ) {
                                                                    date = new Date(
                                                                        historyData.uploadHistory.lastUploadDate
                                                                    );
                                                                }
                                                                // Try parsing as regular date
                                                                else {
                                                                    date = new Date(
                                                                        historyData.uploadHistory.lastUploadDate
                                                                    );
                                                                }
                                                            }
                                                            // Handle Date object
                                                            else if (
                                                                historyData.uploadHistory
                                                                    .lastUploadDate instanceof Date
                                                            ) {
                                                                date =
                                                                    historyData.uploadHistory
                                                                        .lastUploadDate;
                                                            } else {
                                                                return 'Invalid Format';
                                                            }

                                                            if (isNaN(date.getTime())) {
                                                                return 'Invalid Date';
                                                            }

                                                            return date.toLocaleDateString(
                                                                'en-US',
                                                                {
                                                                    year: 'numeric',
                                                                    month: 'short',
                                                                    day: 'numeric',
                                                                }
                                                            );
                                                        } catch (error) {
                                                            console.error(
                                                                'Last Upload date formatting error:',
                                                                error,
                                                                'Value:',
                                                                historyData.uploadHistory
                                                                    .lastUploadDate
                                                            );
                                                            return 'Invalid Date';
                                                        }
                                                    })()}
                                                </Typography>
                                                <Typography
                                                    variant="body1"
                                                    color="white"
                                                    sx={{ fontWeight: 500 }}
                                                >
                                                    Last Upload
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    </Grid>

                                    {/* Download Buttons */}
                                    <Box sx={{ mb: 3 }}>
                                        <Typography variant="subtitle1" gutterBottom>
                                            Download Data
                                        </Typography>
                                        <Stack direction="row" spacing={2}>
                                            <Button
                                                variant="outlined"
                                                color="success"
                                                onClick={() =>
                                                    downloadSuccessfulData(
                                                        historyData.uploadHistory.successfulData ||
                                                            []
                                                    )
                                                }
                                                disabled={
                                                    !historyData.uploadHistory.successfulData ||
                                                    historyData.uploadHistory.successfulData
                                                        .length === 0
                                                }
                                            >
                                                Download Successful Data
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                color="error"
                                                onClick={() =>
                                                    downloadFailedData(
                                                        historyData.uploadHistory.failedData || []
                                                    )
                                                }
                                                disabled={
                                                    !historyData.uploadHistory.failedData ||
                                                    historyData.uploadHistory.failedData.length ===
                                                        0
                                                }
                                            >
                                                Download Failed Data
                                            </Button>
                                        </Stack>
                                    </Box>

                                    {/* Failed Data Details */}
                                    {historyData.uploadHistory.failedData &&
                                        historyData.uploadHistory.failedData.length > 0 && (
                                            <Box>
                                                <Typography variant="subtitle1" gutterBottom>
                                                    Failed Upload Details
                                                </Typography>
                                                <Box
                                                    sx={{
                                                        maxHeight: 300,
                                                        overflow: 'auto',
                                                        border: '1px solid #ddd',
                                                        borderRadius: 1,
                                                    }}
                                                >
                                                    {historyData.uploadHistory.failedData.map(
                                                        (item, index) => (
                                                            <Box
                                                                key={index}
                                                                sx={{
                                                                    p: 2,
                                                                    borderBottom: '1px solid #eee',
                                                                    '&:last-child': {
                                                                        borderBottom: 'none',
                                                                    },
                                                                }}
                                                            >
                                                                <Typography
                                                                    variant="body2"
                                                                    color="error"
                                                                    gutterBottom
                                                                >
                                                                    <strong>Error:</strong>{' '}
                                                                    {item.errorReason}
                                                                </Typography>
                                                                <Typography
                                                                    variant="body2"
                                                                    color="textSecondary"
                                                                    sx={{ fontSize: '0.875rem' }}
                                                                >
                                                                    <strong>Data:</strong>{' '}
                                                                    {item.originalData}
                                                                </Typography>
                                                                <Typography
                                                                    variant="body2"
                                                                    color="textSecondary"
                                                                    sx={{ fontSize: '0.875rem' }}
                                                                >
                                                                    <strong>Failed At:</strong>{' '}
                                                                    {new Date(
                                                                        item.failedAt
                                                                    ).toLocaleString()}
                                                                </Typography>
                                                            </Box>
                                                        )
                                                    )}
                                                </Box>
                                            </Box>
                                        )}
                                </Box>
                            ) : (
                                <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                                    <Typography variant="body2">
                                        No upload history available for this role.
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    )}
                </Box>
            </MainCard>

            {/* Tutorial Modal */}
            <Modal
                open={showTutorial}
                onClose={() => setShowTutorial(false)}
                aria-labelledby="tutorial-modal-title"
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '80%',
                        maxWidth: 800,
                        bgcolor: 'background.paper',
                        borderRadius: 2,
                        boxShadow: 24,
                        p: 4,
                        maxHeight: '90vh',
                        overflow: 'auto',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 2,
                        }}
                    >
                        <Typography variant="h5" id="tutorial-modal-title">
                            Invitation Data Upload Tutorial
                        </Typography>
                        <IconButton onClick={() => setShowTutorial(false)}>
                            <GridCloseIcon />
                        </IconButton>
                    </Box>
                    <video controls width="100%" style={{ borderRadius: '4px' }} src={csvDemo}>
                        Your browser does not support the video tag.
                    </video>
                    <Typography variant="body1" sx={{ mt: 2 }}>
                        This tutorial shows you how to properly format and upload your invitation
                        data. Make sure your CSV file has the correct column headers and data
                        format.
                    </Typography>
                </Box>
            </Modal>
        </Box>
    );
}
